import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Disc3, Play, Clock, BarChart2, MoreHorizontal, ListPlus, Plus } from 'lucide-react'
import { albums, tracks } from '../api/client'
import { usePlayerStore } from '../stores/player'
import LikeButton from '../components/LikeButton'
import TrackMenu from '../components/TrackMenu'
import AddAlbumToPlaylistModal from '../components/AddAlbumToPlaylistModal'
import type { Track } from '../types'

function formatDuration(ms: number | null): string {
  if (!ms) return '--:--'
  const seconds = Math.floor(ms / 1000)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getTotalDuration(tracks: Track[]): string {
  const totalMs = tracks.reduce((sum, t) => sum + (t.duration_ms || 0), 0)
  const totalMins = Math.floor(totalMs / 1000 / 60)
  if (totalMins < 60) {
    return `${totalMins} min`
  }
  const hours = Math.floor(totalMins / 60)
  const mins = totalMins % 60
  return `${hours} hr ${mins} min`
}

export default function Album() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { playQueue, addToQueue } = usePlayerStore()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showPlaylistModal, setShowPlaylistModal] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  const { data: album, isLoading } = useQuery({
    queryKey: ['albums', id],
    queryFn: () => albums.get(id!),
    enabled: !!id,
  })

  // Fetch play counts for all tracks
  const trackIds = album?.tracks.map(t => t.id) || []
  const { data: playCounts } = useQuery({
    queryKey: ['tracks', 'play-counts', trackIds],
    queryFn: () => tracks.getPlayCountsBatch(trackIds),
    enabled: trackIds.length > 0,
    staleTime: 60000, // Cache for 1 minute
  })

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (!album) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Album not found</div>
      </div>
    )
  }

  // Enhance tracks with album details for the player
  const tracksWithDetails = album.tracks.map(track => ({
    ...track,
    artist_name: album.artist_name,
    album_title: album.title,
    cover_path: album.cover_path,
  }))

  const handlePlayAll = () => {
    if (tracksWithDetails.length > 0) {
      playQueue(tracksWithDetails, 0)
    }
  }

  const handlePlayTrack = (index: number) => {
    playQueue(tracksWithDetails, index)
  }

  // Group tracks by disc
  const discs = album.tracks.reduce((acc, track) => {
    const disc = track.disc_number || 1
    if (!acc[disc]) acc[disc] = []
    acc[disc].push(track)
    return acc
  }, {} as Record<number, Track[]>)

  const hasMultipleDiscs = Object.keys(discs).length > 1

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex items-center gap-3 sm:mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-full bg-dark-800 px-3 py-2 text-sm font-medium text-dark-200 transition-colors hover:bg-dark-700 hover:text-white"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        {album.artist_name && (
          <Link
            to={`/artists/${album.artist_id}`}
            className="truncate text-sm text-dark-400 transition-colors hover:text-primary-400"
          >
            {album.artist_name}
          </Link>
        )}
      </div>

      {/* Album header - stacks on mobile */}
      <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="w-40 h-40 sm:w-56 sm:h-56 bg-dark-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 shadow-xl">
          {album.cover_path ? (
            <img
              src={`/api/image/${encodeURIComponent(album.cover_path)}`}
              alt={album.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <Disc3 size={64} className="text-dark-500" />
          )}
        </div>
        <div className="text-center sm:text-left flex-1">
          <p className="text-xs sm:text-sm text-dark-400 uppercase tracking-wider mb-1 sm:mb-2">Album</p>
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <h1 className="text-2xl sm:text-4xl font-bold">{album.title}</h1>
            {/* Album menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-full hover:bg-dark-700 text-dark-400 hover:text-dark-200 transition-colors"
                title="More options"
              >
                <MoreHorizontal size={20} />
              </button>
              {isMenuOpen && (
                <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 w-52 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50 py-1">
                  <button
                    onClick={() => {
                      tracksWithDetails.forEach(track => addToQueue(track))
                      setIsMenuOpen(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-dark-200 hover:bg-dark-700 flex items-center gap-3"
                  >
                    <ListPlus size={16} />
                    Add All to Queue
                  </button>
                  <div className="border-t border-dark-700 my-1" />
                  <button
                    onClick={() => {
                      setShowPlaylistModal(true)
                      setIsMenuOpen(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-dark-200 hover:bg-dark-700 flex items-center gap-3"
                  >
                    <Plus size={16} />
                    Add to Playlist
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-1 text-sm sm:text-base text-dark-300 mt-2">
            {album.artist_name && (
              <Link
                to={`/artists/${album.artist_id}`}
                className="hover:text-primary-400 hover:underline"
              >
                {album.artist_name}
              </Link>
            )}
            {album.year && (
              <>
                <span className="text-dark-500">•</span>
                <span>{album.year}</span>
              </>
            )}
            <span className="text-dark-500">•</span>
            <span>{album.tracks.length} songs</span>
            <span className="text-dark-500 hidden sm:inline">•</span>
            <span className="hidden sm:inline">{getTotalDuration(album.tracks)}</span>
          </div>
        </div>
      </div>

      {/* Play button */}
      {album.tracks.length > 0 && (
        <div className="mb-4 sm:mb-6 flex justify-center sm:justify-start">
          <button
            onClick={handlePlayAll}
            className="btn btn-primary flex items-center gap-2"
          >
            <Play size={18} />
            Play
          </button>
        </div>
      )}

      {/* Track list - Table for desktop, cards for mobile */}
      <div className="card p-0 overflow-hidden">
        {Object.entries(discs)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([discNum, discTracks]) => (
            <div key={discNum}>
              {hasMultipleDiscs && (
                <div className="px-4 py-2 text-dark-400 text-sm font-medium border-b border-dark-700">
                  Disc {discNum}
                </div>
              )}

              {/* Desktop table */}
              <table className="w-full hidden sm:table">
                <thead>
                  <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                    <th className="py-3 px-4 w-12">#</th>
                    <th className="py-3">Title</th>
                    <th className="py-3 px-4 w-20"></th>
                    <th className="py-3 px-4 text-right" title="Play count">
                      <BarChart2 size={16} />
                    </th>
                    <th className="py-3 px-4 text-right">
                      <Clock size={16} />
                    </th>
                    <th className="py-3 px-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {discTracks.map((track, index) => {
                    const globalIndex = album.tracks.findIndex((t) => t.id === track.id)
                    const playCount = playCounts?.[track.id] || 0
                    const trackWithDetails = tracksWithDetails[globalIndex]
                    return (
                      <tr
                        key={track.id}
                        className="group hover:bg-dark-700/50 cursor-pointer"
                        onClick={() => handlePlayTrack(globalIndex)}
                      >
                        <td className="py-3 px-4 text-dark-400 group-hover:text-dark-100">
                          <span className="group-hover:hidden">
                            {track.track_number || index + 1}
                          </span>
                          <Play size={16} className="hidden group-hover:block" />
                        </td>
                        <td className="py-3 font-medium">{track.title}</td>
                        <td className="py-3 px-4">
                          <LikeButton trackId={track.id} />
                        </td>
                        <td className="py-3 px-4 text-dark-500 text-right text-sm">
                          {playCount > 0 ? playCount : '-'}
                        </td>
                        <td className="py-3 px-4 text-dark-400 text-right">
                          {formatDuration(track.duration_ms)}
                        </td>
                        <td className="py-3 px-2">
                          <TrackMenu track={trackWithDetails} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              {/* Mobile list */}
              <div className="sm:hidden divide-y divide-dark-700">
                {discTracks.map((track, index) => {
                  const globalIndex = album.tracks.findIndex((t) => t.id === track.id)
                  const playCount = playCounts?.[track.id] || 0
                  const trackWithDetails = tracksWithDetails[globalIndex]
                  return (
                    <div
                      key={track.id}
                      className="flex items-center gap-3 p-3 hover:bg-dark-700/50 cursor-pointer"
                      onClick={() => handlePlayTrack(globalIndex)}
                    >
                      <span className="w-6 text-dark-400 text-sm text-center">
                        {track.track_number || index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{track.title}</p>
                        <p className="text-xs text-dark-400">
                          {formatDuration(track.duration_ms)}
                          {playCount > 0 && <span className="ml-2">• {playCount} plays</span>}
                        </p>
                      </div>
                      <LikeButton trackId={track.id} />
                      <TrackMenu track={trackWithDetails} />
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
      </div>

      <AddAlbumToPlaylistModal
        albumTitle={album.title}
        albumCover={album.cover_path}
        tracks={album.tracks}
        isOpen={showPlaylistModal}
        onClose={() => setShowPlaylistModal(false)}
      />
    </div>
  )
}
